import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { dataSelector } from "../../shared/api/catalogos.api";
import { DataContext } from "./DataContext";
import {
  usuarioObtenerCompaniasYSucursales,
  usuarioObtenerRutasDeAcceso,
} from "../../modules/usuarios";

export const DataProvider = ({ children }: { children: ReactNode }) => {
  const [listaCompanias, setListaCompanias] = useState<any[]>([]);
  const [listaSucursales, setListaSucursales] = useState<any[]>([]);
  const [listaSedesPorUsuario, setListaSedesPorUsuario] = useState<any[]>([]);
  const [listaTipoDocumento, setListaTipoDocumento] = useState<any[]>([]);
  const [listaUbigeo, setListaUbigeo] = useState<any[]>([]);
  const [listaRutasDeAcceso, setListaRutasDeAcceso] = useState<any[]>([]);
  // OPT-GUARD-NAV (ítem 7): estado de la carga de RutaPlana. El guard de navegación deriva el
  // acceso EN MEMORIA de `listaRutasDeAcceso` (misma fuente que ya gobierna los botones de
  // acción), sin un round-trip bloqueante por navegación. 'sesion-vencida' = token inválido.
  const [rutasEstado, setRutasEstado] = useState<"cargando" | "cargado" | "sesion-vencida">("cargando");

  const getDataSelector = useCallback(async (): Promise<void> => {
    const { tiposIdentificacion, ubigeos } = await dataSelector();
    setListaTipoDocumento(tiposIdentificacion);
    setListaUbigeo(ubigeos);
  }, []);

  const getCompaniasYSedesPorUsuario = useCallback(async (token: string) => {
    const dataPOST = { token };
    const { companias, sucursales, sedes } = await usuarioObtenerCompaniasYSucursales(dataPOST);

    setListaCompanias(companias);
    setListaSucursales(sucursales);
    setListaSedesPorUsuario(sedes);
    return {
      companias: companias,
      sucursales: sucursales,
      primeraCompania: companias?.[0]?.id,
      primeraSucursal: sucursales?.[0]?.id,
    };
  }, []);

  // Cache de rutas por token. `RutaPlana` (permisos) es costoso (~13 s por N+1 en el
  // backend). AppRouter lo invoca en CADA navegación; aquí se pide UNA sola vez por sesión
  // (por token) y se ignoran las llamadas repetidas para el mismo token. Si cambia el token
  // (nuevo login), se vuelve a pedir. Ver Incidencias/ANALISIS-CARGA-LENTA-TABLAS-SEGURIDAD-2026-07-31.md
  const rutasCacheRef = useRef<{ cargando: boolean; token: string | null }>({ cargando: false, token: null });

  const getRutasDeAcceso = useCallback(async (token: string, parametro: string): Promise<void> => {
    if (rutasCacheRef.current.cargando) return;
    if (rutasCacheRef.current.token === token && listaRutasDeAcceso.length > 0) return;
    // Cambio de usuario (token distinto al cacheado): invalida las rutas viejas para NO derivar
    // el guard con permisos de la sesión anterior mientras se recargan (evita ventana stale).
    if (rutasCacheRef.current.token && rutasCacheRef.current.token !== token) {
      setListaRutasDeAcceso([]);
      setRutasEstado("cargando");
    }
    rutasCacheRef.current.cargando = true;
    try {
      const res: any = await usuarioObtenerRutasDeAcceso({ token, parametro });
      if (res?.status === 1 && Array.isArray(res.rutas)) {
        setListaRutasDeAcceso(res.rutas);
        rutasCacheRef.current.token = token;
        setRutasEstado("cargado");
      } else if (res?.status === 0 && res?.message === "El token es invalido") {
        // Token inválido/expirado (el guard `validarToken` en enforce responde así): sesión
        // vencida → el AppRouter cierra sesión. Mismo criterio que antes usaba VerificarUseMod.
        setRutasEstado("sesion-vencida");
      } else {
        // status 0 sin token-inválido (usuario sin rutas) o -1 (error servidor): se marca
        // "cargado" con la lista vigente; el guard mostrará SinAcceso (fail-closed, como antes)
        // y se reintenta en la próxima navegación porque la lista sigue vacía.
        setRutasEstado("cargado");
      }
    } finally {
      rutasCacheRef.current.cargando = false;
    }
  }, [listaRutasDeAcceso]);

  useEffect(() => {
    getDataSelector();
    // carga inicial (una sola vez al montar)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memoizado: sin esto, el objeto `value` se recrea en CADA render del provider y
  // re-renderiza a TODOS los consumidores del contexto aunque su dato no cambie.
  // Las funciones son estables (useCallback) y los setState de React ya lo son.
  const value = useMemo(
    () => ({
      listaCompanias,
      setListaCompanias,
      listaSucursales,
      setListaSucursales,
      listaSedesPorUsuario,
      setListaSedesPorUsuario,
      listaTipoDocumento,
      setListaTipoDocumento,
      listaUbigeo,
      setListaUbigeo,
      getDataSelector,
      getCompaniasYSedesPorUsuario,
      getRutasDeAcceso,
      listaRutasDeAcceso,
      rutasEstado,
    }),
    [
      listaCompanias,
      listaSucursales,
      listaSedesPorUsuario,
      listaTipoDocumento,
      listaUbigeo,
      listaRutasDeAcceso,
      getDataSelector,
      getCompaniasYSedesPorUsuario,
      getRutasDeAcceso,
      rutasEstado,
    ]
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
